import { Link } from 'react-router-dom';
import ScrollSceneSection, { ModelCredit, type SceneStage } from './ScrollSceneSection';
import type { LaptopScene } from './laptopScene';
import { PRICES, shekel } from '@/data/prices';

/**
 * Homepage section (2026-10-10): a laptop that opens as you scroll, boots and
 * runs a diagnosis on its own screen — laptop repair is the most common job.
 * Layout and loading: ScrollSceneSection; the 3D side: laptopScene.ts. Loads
 * when the visitor gets within 800px (not eagerly like the PC above).
 */
const STAGES: SceneStage[] = [
  { at: 0, title: 'מחשב נייד שהפסיק לעבוד?', text: 'גללו, ונפתח ונבדוק אותו יחד.' },
  {
    at: 0.1, title: 'פותחים בזהירות',
    text: 'צירים, מסך ומקלדת: החלקים שנשברים הכי הרבה במחשב נייד. ברוב המקרים מתקנים בלי להחליף את כל המחשב.',
    link: { to: '/services', label: 'תיקוני מחשבים ניידים' },
  },
  { at: 0.36, title: 'מדליקים ובודקים', text: 'סוללה, מקלדת, מסך, קירור וכונן. בודקים הכול, לא רק את מה שנראה שבור.' },
  {
    at: 0.52, title: 'רואים בדיוק מה הבעיה',
    text: 'מחשב נייד שמתחמם ונכבה? לרוב זה אבק במאוורר. ניקוי והחלפת משחה טרמית מחזירים אותו לחיים.',
    link: { to: '/diagnose', label: 'ייעוץ חינם' },
  },
  { at: 0.88, title: 'ותשובה כנה: לתקן או לא', text: `אם התיקון לא משתלם, נגיד לכם את זה. בדיקה במעבדה עולה ${shekel(PRICES.labCheck.min)}, ומחיר התיקון ידוע לפני שמתחילים.` },
];

const credit = (
  <ModelCredit author="Yolala3D" url="https://sketchfab.com/3d-models/gaming-laptop-4e72a2078b3c4a75a821ab09830693fe" />
);

export default function LaptopSection() {
  return (
    <ScrollSceneSection<LaptopScene>
      id="laptop-check"
      stages={STAGES}
      poster="/models/laptop-poster.webp"
      heightClass="h-[300vh] md:h-[360vh]"
      create={async (host, onFrame) => (await import('./laptopScene')).createLaptopScene(host, { onFrame })}
      finalCta={
        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/diagnose" className="cin-btn-primary !py-3 !px-6">ייעוץ חינם</Link>
          <Link to="/services" className="cin-btn-ghost cin-btn-on-dark !py-3 !px-6">כל השירותים</Link>
        </div>
      }
      credit={credit}
      staticBody={
        <>
          <h2 className="cin-display !text-[clamp(2rem,4vw,3.25rem)] mb-4 text-white">{STAGES[0].title}</h2>
          <p className="max-w-2xl text-white/70 text-lg mb-10">{STAGES[2].text}</p>
          <img
            src="/models/laptop-poster.webp"
            alt="מחשב נייד פתוח עם מסך אבחון: סוללה, מקלדת, מסך, קירור וכונן"
            width={1600}
            height={1000}
            loading="lazy"
            decoding="async"
            className="w-full max-w-3xl h-auto rounded-2xl mb-12"
          />
          <ol className="grid gap-6 sm:grid-cols-2">
            {[STAGES[1], STAGES[3], STAGES[4]].map((s) => (
              <li key={s.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <h3 className="text-xl font-bold mb-2">{s.title}</h3>
                <p className="text-white/70 leading-relaxed mb-3">{s.text}</p>
                {s.link && <Link to={s.link.to} className="text-[#8FB3FF] font-semibold hover:underline">{s.link.label} ←</Link>}
              </li>
            ))}
          </ol>
        </>
      }
    />
  );
}
